import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconArrowRight,
  IconBrackets,
  IconCode,
  IconCoin,
  IconDatabase,
  IconInfoCircle,
  IconListDetails,
  IconPackage,
  IconShoppingCart,
  IconTags,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  flowStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderLeft: `2px solid ${theme.colors.cyan[7]}`,
    marginLeft: rem(10),
    "&:last-of-type": {
      borderLeft: "none",
    },
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export function GamificationDocumentation() {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Gamification Documentation"
        subtitle="Complete reference for the virtual economy and gamification system"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#currencies">Currencies</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#items">Items & Categories</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#shops">Shops</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#products">Shop Products</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#player-economy">Player Economy</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#purchasing">Purchasing</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCoin size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The Gamification API provides a complete virtual economy system for
            your platform. It includes currencies, items, shops, player
            inventories, and purchase flows. All endpoints are available through
            the GraphQL API and support pagination, filtering by IDs and
            external IDs, and custom metadata.
          </Text>
          <Text mb="md">
            The system is composed of the following entity hierarchy:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Gamification System
├── Currencies[]          (virtual monetary units)
├── Items[]               (ownable objects: cosmetics, consumables, etc.)
│   └── Item Categories[] (grouping for items)
└── Shops[]               (storefronts for purchasing)
    ├── Shop Categories[] (per-shop product grouping)
    └── Shop Products[]   (purchasable bundles of items/currencies)
         └── Product Items[] (items or currencies given on purchase)

Player Economy
├── Player Currencies[]   (per-player balance for each currency)
├── Player Items[]        (per-player inventory with quantities)
└── Purchase History[]    (audit trail of all purchases)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            All gamification entities support <Code>metadata</Code> (arbitrary
            key-value pairs) and an optional <Code>externalId</Code> for linking
            to external systems. All list queries support filtering by{" "}
            <Code>ids</Code> and <Code>externalIds</Code>.
          </Alert>
        </div>

        {/* ===================== CURRENCIES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconWallet size={18} />}
            title="Currencies"
            id="currencies"
          />
          <Text mb="md">
            Currencies represent virtual monetary units in your economy. Each
            currency has a name, a display symbol, and an optional description.
            Currencies can be marked as <Code>hidden</Code> to keep them
            invisible to players while still being usable through the API.
          </Text>

          <Title order={4} mb="xs">
            Currency Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>id</Code>
                </td>
                <td>ID!</td>
                <td>Unique identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Display name</td>
              </tr>
              <tr>
                <td>
                  <Code>symbol</Code>
                </td>
                <td>String!</td>
                <td>Currency symbol (e.g., $, G)</td>
              </tr>
              <tr>
                <td>
                  <Code>description</Code>
                </td>
                <td>String!</td>
                <td>Description of the currency</td>
              </tr>
              <tr>
                <td>
                  <Code>hidden</Code>
                </td>
                <td>Boolean!</td>
                <td>Whether the currency is hidden from players</td>
              </tr>
              <tr>
                <td>
                  <Code>externalId</Code>
                </td>
                <td>String</td>
                <td>Optional external system identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>metadata</Code>
                </td>
                <td>[PropertyValue]</td>
                <td>Custom key-value pairs</td>
              </tr>
            </tbody>
          </Table>

          <GqlBlock>
            {`# List currencies
query ($cursor: String, $count: Float) {
  currencies(page: { first: $count, after: $cursor }) {
    nodes { id, name, symbol, description, hidden, externalId, metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# Create currency
mutation ($input: CurrencyCreateInput!) {
  currencyCreate(input: $input) { id }
}

# Update currency
mutation ($id: ID!, $input: CurrencyUpdateInput!) {
  currencyUpdate(id: $id, input: $input) { id }
}

# Delete currency
mutation ($id: ID!) {
  currencyDelete(id: $id)
}`}
          </GqlBlock>
        </div>

        {/* ===================== ITEMS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPackage size={18} />}
            title="Items & Categories"
            id="items"
          />
          <Text mb="md">
            Items represent anything that can be owned by a player — cosmetics,
            consumables, power-ups, or any custom concept. Items can optionally
            belong to a category for organization and filtering.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Items
              </Text>
              <Text size="sm" mb="xs">
                Individual ownable objects in your economy:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>Name, description, and optional category</List.Item>
                <List.Item>
                  Optional <Code>externalId</Code> for external linking
                </List.Item>
                <List.Item>
                  Custom <Code>metadata</Code> for images, colors, etc.
                </List.Item>
                <List.Item>
                  Filter by <Code>ids</Code>, <Code>externalIds</Code>, or{" "}
                  <Code>categoryIds</Code>
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Item Categories
              </Text>
              <Text size="sm" mb="xs">
                Grouping mechanism for organizing items:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Shared across all shops (global categories)
                </List.Item>
                <List.Item>Name, description, and metadata</List.Item>
                <List.Item>
                  Filter by <Code>ids</Code> or <Code>externalIds</Code>
                </List.Item>
                <List.Item>
                  Separate from shop categories (which are per-shop)
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Item Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>id</Code>
                </td>
                <td>ID!</td>
                <td>Unique identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Display name</td>
              </tr>
              <tr>
                <td>
                  <Code>description</Code>
                </td>
                <td>String!</td>
                <td>Item description</td>
              </tr>
              <tr>
                <td>
                  <Code>categoryId</Code>
                </td>
                <td>ID</td>
                <td>Optional item category reference</td>
              </tr>
              <tr>
                <td>
                  <Code>externalId</Code>
                </td>
                <td>String</td>
                <td>Optional external system identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>metadata</Code>
                </td>
                <td>[PropertyValue]</td>
                <td>Custom key-value pairs</td>
              </tr>
            </tbody>
          </Table>

          <GqlBlock>
            {`# List items
query ($cursor: String, $count: Float) {
  items(page: { first: $count, after: $cursor }) {
    nodes { id, name, description, categoryId, externalId, metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# CRUD mutations
mutation { itemCreate(input: $input) { id } }
mutation { itemUpdate(id: $id, input: $input) { id } }
mutation { itemDelete(id: $id) }

# Item Categories
query { itemCategories(page: $page) { nodes { id, name, description } ... } }
mutation { itemCategoryCreate(input: $input) { id } }
mutation { itemCategoryUpdate(id: $id, input: $input) { id } }
mutation { itemCategoryDelete(id: $id) }`}
          </GqlBlock>
        </div>

        {/* ===================== SHOPS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconShoppingCart size={18} />}
            title="Shops"
            id="shops"
          />
          <Text mb="md">
            Shops are storefronts where players can browse and purchase
            products. Each shop is a container for products and shop-specific
            categories. You can create multiple shops for different purposes — a
            main shop, a seasonal event shop, a VIP-only shop, etc.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Shops
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>Name, description, and metadata</List.Item>
                <List.Item>
                  Container for products and shop categories
                </List.Item>
                <List.Item>
                  Filter by <Code>ids</Code> or <Code>externalIds</Code>
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Shop Categories
              </Text>
              <Text size="sm" mb="xs">
                Per-shop product grouping (separate from item categories):
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Scoped to a single shop via <Code>shopId</Code>
                </List.Item>
                <List.Item>Used to organize products within a shop</List.Item>
                <List.Item>
                  Filter by <Code>ids</Code>, <Code>externalIds</Code>, or{" "}
                  <Code>shopIds</Code>
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <GqlBlock>
            {`# List shops
query { shops(page: $page) { nodes { id, name, description } ... } }
mutation { shopCreate(input: $input) { id } }
mutation { shopUpdate(id: $id, input: $input) { id } }
mutation { shopDelete(id: $id) }

# Shop categories (per-shop)
query { shopCategories(filter: { shopIds: [$shopId] }, page: $page) { nodes { id, name } ... } }
mutation { shopCategoryCreate(input: $input) { id } }   # requires shopId
mutation { shopCategoryUpdate(id: $id, input: $input) { id } }
mutation { shopCategoryDelete(id: $id) }`}
          </GqlBlock>
        </div>

        {/* ===================== PRODUCTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTags size={18} />}
            title="Shop Products"
            id="products"
          />
          <Text mb="md">
            Products define what is available for purchase in a shop. Each
            product can contain one or more items or currencies as rewards, and
            has a price defined in virtual currencies and/or real money.
          </Text>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="md"
          >
            Products support both virtual currency prices (via{" "}
            <Code>currencyPrices</Code>) and real money prices (via{" "}
            <Code>price</Code> + <Code>currency</Code> with USD/EUR/GBP). A
            single product can have multiple virtual currency costs.
          </Alert>

          <Title order={4} mb="xs">
            Product Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Product display name</td>
              </tr>
              <tr>
                <td>
                  <Code>description</Code>
                </td>
                <td>String!</td>
                <td>Product description</td>
              </tr>
              <tr>
                <td>
                  <Code>items</Code>
                </td>
                <td>[ShopItemQuantity]</td>
                <td>
                  Items or currencies given on purchase. Each entry has an{" "}
                  <Code>id</Code>, <Code>quantity</Code>, and <Code>type</Code>{" "}
                  (ITEM or CURRENCY).
                </td>
              </tr>
              <tr>
                <td>
                  <Code>currencyPrices</Code>
                </td>
                <td>[EntityQuantity]</td>
                <td>
                  Virtual currency costs. Each entry has a currency{" "}
                  <Code>id</Code> and <Code>quantity</Code>.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>price</Code>
                </td>
                <td>Float</td>
                <td>Real money price (optional)</td>
              </tr>
              <tr>
                <td>
                  <Code>currency</Code>
                </td>
                <td>ShopCurrencies</td>
                <td>Real money currency: USD, EUR, or GBP</td>
              </tr>
              <tr>
                <td>
                  <Code>categoryId</Code>
                </td>
                <td>ID</td>
                <td>Optional shop category for organization</td>
              </tr>
              <tr>
                <td>
                  <Code>visibleAt</Code>
                </td>
                <td>DateTime</td>
                <td>Optional date when product becomes visible to players</td>
              </tr>
              <tr>
                <td>
                  <Code>configuration</Code>
                </td>
                <td>ShopProductConfiguration</td>
                <td>
                  Purchase limits: <Code>minQuantity</Code>,{" "}
                  <Code>maxQuantity</Code>, <Code>maxBoughtQuantity</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>externalId</Code>
                </td>
                <td>String</td>
                <td>Optional external system identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>metadata</Code>
                </td>
                <td>[PropertyValue]</td>
                <td>Custom key-value pairs</td>
              </tr>
            </tbody>
          </Table>

          <GqlBlock>
            {`# List products for a shop
query ($shopId: ID!, $cursor: String, $count: Float) {
  shopProducts(shopId: $shopId, page: { first: $count, after: $cursor }) {
    nodes { id, name, description, price, currency, visibleAt, externalId,
            currencyPrices { id, quantity },
            items { id, quantity, type },
            configuration { minQuantity, maxQuantity, maxBoughtQuantity } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# List items within a product
query ($shopProductId: ID!) {
  shopProductItems(shopProductId: $shopProductId, page: $page) {
    nodes { id, quantity, type }
  }
}

# CRUD mutations
mutation { shopProductCreate(shopId: $shopId, input: $input) { id } }
mutation { shopProductUpdate(productId: $productId, input: $input) { id } }
mutation { shopProductDelete(productId: $productId) }`}
          </GqlBlock>
        </div>

        {/* ===================== PLAYER ECONOMY ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUser size={18} />}
            title="Player Economy"
            id="player-economy"
          />
          <Text mb="md">
            The player economy tracks individual player balances and
            inventories. These are automatically updated when purchases are
            made, but can also be managed directly through the API for manual
            adjustments, rewards, or administrative actions.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="cyan">Player Currencies</Badge>
              </Group>
              <Text size="sm" mb="xs">
                Per-player balance for each currency. Supports three operations:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>add</Code> — Add to the current balance
                </List.Item>
                <List.Item>
                  <Code>remove</Code> — Subtract from the current balance
                </List.Item>
                <List.Item>
                  <Code>forceBalance</Code> — Set the balance to an exact value
                </List.Item>
              </List>
              <Alert
                icon={<IconAlertCircle />}
                color="yellow"
                variant="light"
                mt="sm"
                p="xs"
              >
                <Text size="xs">
                  <Code>forceBalance</Code> overwrites the balance completely.
                  Use <Code>add</Code> and <Code>remove</Code> for safe
                  incremental adjustments.
                </Text>
              </Alert>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="green">Player Items (Inventory)</Badge>
              </Group>
              <Text size="sm" mb="xs">
                Per-player item ownership with quantities. Supports three
                operations:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>add</Code> — Add quantity to the player&apos;s inventory
                </List.Item>
                <List.Item>
                  <Code>remove</Code> — Remove quantity from inventory
                </List.Item>
                <List.Item>
                  <Code>set</Code> — Set the quantity to an exact value
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Player Currency Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>id</Code>
                </td>
                <td>ID!</td>
                <td>Unique identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>playerProfileId</Code>
                </td>
                <td>ID!</td>
                <td>Player profile reference</td>
              </tr>
              <tr>
                <td>
                  <Code>currencyId</Code>
                </td>
                <td>ID!</td>
                <td>Currency reference</td>
              </tr>
              <tr>
                <td>
                  <Code>balance</Code>
                </td>
                <td>Float!</td>
                <td>Current balance</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Player Item Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>id</Code>
                </td>
                <td>ID!</td>
                <td>Unique identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>playerProfileId</Code>
                </td>
                <td>ID!</td>
                <td>Player profile reference</td>
              </tr>
              <tr>
                <td>
                  <Code>itemId</Code>
                </td>
                <td>ID!</td>
                <td>Item reference</td>
              </tr>
              <tr>
                <td>
                  <Code>quantity</Code>
                </td>
                <td>Int!</td>
                <td>Current quantity owned</td>
              </tr>
              <tr>
                <td>
                  <Code>metadata</Code>
                </td>
                <td>[PropertyValue]</td>
                <td>Custom key-value pairs</td>
              </tr>
            </tbody>
          </Table>

          <GqlBlock>
            {`# Player currencies
query ($filter: PlayerCurrencyFilter, $page: PageInfo!) {
  playerCurrencies(filter: $filter, page: $page) {
    nodes { id, playerProfileId, currencyId, balance }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter by playerProfileIds or currencyIds

mutation ($playerId: ID!, $input: PlayerCurrencyUpdateInput!) {
  playerCurrencyUpdate(playerId: $playerId, input: $input) { id, balance }
}
# Input: { currencies: [{ currencyId, add, remove, forceBalance }] }

# Player items (inventory)
query ($filter: PlayerItemFilter, $page: PageInfo!) {
  playerItems(filter: $filter, page: $page) {
    nodes { id, playerProfileId, itemId, quantity, metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter by playerProfileIds or itemIds

mutation ($playerId: ID!, $input: PlayerItemsUpdateInput!) {
  playerItemsUpdate(playerId: $playerId, input: $input) { id, quantity }
}
# Input: { items: [{ itemId, add, remove, set }] }`}
          </GqlBlock>
        </div>

        {/* ===================== PURCHASING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconArrowRight size={18} />}
            title="Purchase Flow"
            id="purchasing"
          />
          <Text mb="md">
            The purchase mutation handles the complete purchase flow atomically.
            When a purchase is made, the system processes the entire transaction
            as a single operation.
          </Text>

          <Title order={4} mb="xs">
            Purchase Steps
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`Player initiates purchase
    |
    v
1. Verify player has enough currency for all items
    |
    v
2. Deduct currency amounts from player's balances
    |
    v
3. Add purchased items/currencies to player's account
    |
    v
4. Record transaction in purchase history`}
            </GqlBlock>
          </Card>

          <GqlBlock>
            {`mutation ($shopId: ID!, $input: PlayerShopProductPurchaseInput!, $paymentConfig: PaymentConfigInput) {
  playerShopProductPurshase(shopId: $shopId, input: $input, paymentConfig: $paymentConfig) {
    id
  }
}

# Input: { items: [{ shopProductId: ID!, quantity: Float! }] }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            For real-money purchases, the mutation returns a{" "}
            <Code>PaymentUrlAndClientSecret</Code> containing the payment URL
            and client secret for completing the payment through the external
            payment provider. Use the optional <Code>paymentConfig</Code>{" "}
            parameter to configure the payment flow.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Purchase History
          </Title>
          <Text mb="sm">
            All purchases are recorded and can be queried for audit and
            analytics purposes.
          </Text>

          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>id</Code>
                </td>
                <td>ID!</td>
                <td>Unique purchase identifier</td>
              </tr>
              <tr>
                <td>
                  <Code>playerProfileId</Code>
                </td>
                <td>ID!</td>
                <td>Player who made the purchase</td>
              </tr>
              <tr>
                <td>
                  <Code>shopProductId</Code>
                </td>
                <td>ID!</td>
                <td>Product that was purchased</td>
              </tr>
              <tr>
                <td>
                  <Code>quantity</Code>
                </td>
                <td>Float!</td>
                <td>Number of units purchased</td>
              </tr>
              <tr>
                <td>
                  <Code>createdAt</Code>
                </td>
                <td>DateTime!</td>
                <td>When the purchase was made</td>
              </tr>
            </tbody>
          </Table>

          <GqlBlock>
            {`query ($filter: PlayerShopProductPurchaseFilter, $page: PageInfo!) {
  playerShopProductPurshases(filter: $filter, page: $page) {
    nodes { id, playerProfileId, shopProductId, quantity, createdAt }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter by ids, playerProfileIds, shopIds, or shopProductIds`}
          </GqlBlock>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations used by the gamification
            system.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="currency-ops">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    CURRENCIES
                  </Badge>
                  <Text>Currency Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      currencies (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float, $filter: CurrencyFilter) {
  currencies(filter: $filter, page: { first: $count, after: $cursor }) {
    nodes { id, name, symbol, description, hidden, externalId,
            metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { ids: [ID], externalIds: [String] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      currencyCreate
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CurrencyCreateInput!) {
  currencyCreate(input: $input) { id, name, symbol }
}
# Required: name, symbol, description, hidden, metadata
# Optional: externalId`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      currencyUpdate / currencyDelete
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: CurrencyUpdateInput!) {
  currencyUpdate(id: $id, input: $input) { id }
}

mutation ($id: ID!) {
  currencyDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="item-ops">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    ITEMS
                  </Badge>
                  <Text>Item & Category Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      items (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float, $filter: ItemFilter) {
  items(filter: $filter, page: { first: $count, after: $cursor }) {
    nodes { id, name, description, categoryId, externalId,
            metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { ids: [ID], externalIds: [String], categoryIds: [ID] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Item CRUD
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: ItemCreateInput!) {
  itemCreate(input: $input) { id }
}
# Required: name, description, metadata
# Optional: categoryId, externalId

mutation ($id: ID!, $input: ItemUpdateInput!) {
  itemUpdate(id: $id, input: $input) { id }
}

mutation ($id: ID!) {
  itemDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Item Categories
                    </Text>
                    <GqlBlock>
                      {`query ($filter: ItemCategoryFilter, $page: PageInfo!) {
  itemCategories(filter: $filter, page: $page) {
    nodes { id, name, description, externalId, metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

mutation { itemCategoryCreate(input: $input) { id } }
mutation { itemCategoryUpdate(id: $id, input: $input) { id } }
mutation { itemCategoryDelete(id: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="shop-ops">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    SHOPS
                  </Badge>
                  <Text>Shop, Category & Product Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      shops (list)
                    </Text>
                    <GqlBlock>
                      {`query ($filter: ShopFilter, $page: PageInfo!) {
  shops(filter: $filter, page: $page) {
    nodes { id, name, description, externalId, metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Shop CRUD
                    </Text>
                    <GqlBlock>
                      {`mutation { shopCreate(input: $input) { id } }
mutation { shopUpdate(id: $id, input: $input) { id } }
mutation { shopDelete(id: $id) }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Shop Categories
                    </Text>
                    <GqlBlock>
                      {`query ($filter: ShopCategoryFilter, $page: PageInfo!) {
  shopCategories(filter: $filter, page: $page) {
    nodes { id, name, description, shopId }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { ids, externalIds, shopIds }

mutation { shopCategoryCreate(input: $input) { id } }   # requires shopId
mutation { shopCategoryUpdate(id: $id, input: $input) { id } }
mutation { shopCategoryDelete(id: $id) }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Shop Products
                    </Text>
                    <GqlBlock>
                      {`query ($shopId: ID!, $filter: ShopProductFilter, $page: PageInfo!) {
  shopProducts(shopId: $shopId, filter: $filter, page: $page) {
    nodes { id, name, description, price, currency, visibleAt,
            items { id, quantity, type },
            currencyPrices { id, quantity },
            configuration { minQuantity, maxQuantity, maxBoughtQuantity } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { ids, externalIds, categoryIds }

query ($shopProductId: ID!, $page: PageInfo!) {
  shopProductItems(shopProductId: $shopProductId, page: $page) {
    nodes { id, quantity, type }
  }
}

mutation { shopProductCreate(shopId: $shopId, input: $input) { id } }
mutation { shopProductUpdate(productId: $id, input: $input) { id } }
mutation { shopProductDelete(productId: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="player-ops">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="violet" className={classes.mutationBadge}>
                    PLAYER
                  </Badge>
                  <Text>Player Economy Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      playerCurrencies (balances)
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerCurrencyFilter, $page: PageInfo!) {
  playerCurrencies(filter: $filter, page: $page) {
    nodes { id, playerProfileId, currencyId, balance }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { playerProfileIds: [ID], currencyIds: [ID] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerCurrencyUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($playerId: ID!, $input: PlayerCurrencyUpdateInput!) {
  playerCurrencyUpdate(playerId: $playerId, input: $input) {
    id, balance
  }
}

# Input structure:
# {
#   currencies: [{
#     currencyId: ID!
#     add: Float          # Add to balance
#     remove: Float       # Subtract from balance
#     forceBalance: Float # Set exact balance (overwrites!)
#   }]
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerItems (inventory)
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerItemFilter, $page: PageInfo!) {
  playerItems(filter: $filter, page: $page) {
    nodes { id, playerProfileId, itemId, quantity,
            metadata { property, value } }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { playerProfileIds: [ID], itemIds: [ID] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerItemsUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($playerId: ID!, $input: PlayerItemsUpdateInput!) {
  playerItemsUpdate(playerId: $playerId, input: $input) {
    id, quantity
  }
}

# Input structure:
# {
#   items: [{
#     itemId: ID!
#     add: Int      # Add to quantity
#     remove: Int   # Remove from quantity
#     set: Int      # Set exact quantity
#   }]
# }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="purchase-ops">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    PURCHASE
                  </Badge>
                  <Text>Purchase & History Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      playerShopProductPurshase
                    </Text>
                    <GqlBlock>
                      {`mutation ($shopId: ID!, $input: PlayerShopProductPurchaseInput!,
         $paymentConfig: PaymentConfigInput) {
  playerShopProductPurshase(
    shopId: $shopId
    input: $input
    paymentConfig: $paymentConfig
  ) {
    id
  }
}

# Input: { items: [{ shopProductId: ID!, quantity: Float! }] }
# Returns PaymentUrlAndClientSecret for real-money purchases`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerShopProductPurshases (history)
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerShopProductPurchaseFilter, $page: PageInfo!) {
  playerShopProductPurshases(filter: $filter, page: $page) {
    nodes { id, playerProfileId, shopProductId, quantity, createdAt }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}
# Filter: { ids, playerProfileIds, shopIds, shopProductIds }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used throughout the gamification module. All
            types are generated from the GraphQL schema via codegen.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      ShopItemType
                    </Text>
                    <GqlBlock>
                      {`ITEM
CURRENCY`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      ShopCurrencies (real money)
                    </Text>
                    <GqlBlock>
                      {`USD
EUR
GBP`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      PropertyType (metadata)
                    </Text>
                    <GqlBlock>
                      {`STRING
NUMBER
BOOLEAN
DATE
COUNTRY
EMAIL
PHONE
URL`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Currency
├── id: ID!
├── name: String!
├── symbol: String!
├── description: String!
├── hidden: Boolean!
├── externalId: String
└── metadata: [PropertyValue]

Item
├── id: ID!
├── name: String!
├── description: String!
├── categoryId: ID
├── externalId: String
└── metadata: [PropertyValue]

ItemCategory
├── id: ID!
├── name: String!
├── description: String!
├── externalId: String
└── metadata: [PropertyValue]

Shop
├── id: ID!
├── name: String!
├── description: String!
├── externalId: String
├── metadata: [PropertyValue]
├── categories: [ShopCategory]
│   ├── id: ID!
│   ├── name: String!
│   ├── description: String!
│   └── shopId: ID!
└── products: [ShopProduct]
    ├── id: ID!
    ├── name: String!
    ├── description: String!
    ├── price: Float
    ├── currency: ShopCurrencies
    ├── visibleAt: DateTime
    ├── categoryId: ID
    ├── externalId: String
    ├── metadata: [PropertyValue]
    ├── items: [ShopItemQuantity]
    │   ├── id: ID!
    │   ├── quantity: Float!
    │   └── type: ShopItemType!  (ITEM or CURRENCY)
    ├── currencyPrices: [EntityQuantity]
    │   ├── id: ID!  (currency ID)
    │   └── quantity: Float!
    └── configuration: ShopProductConfiguration
        ├── minQuantity: Int
        ├── maxQuantity: Int
        └── maxBoughtQuantity: Int

PlayerCurrency
├── id: ID!
├── playerProfileId: ID!
├── currencyId: ID!
└── balance: Float!

PlayerItem
├── id: ID!
├── playerProfileId: ID!
├── itemId: ID!
├── quantity: Int!
└── metadata: [PropertyValue]

PlayerShopProductPurchase
├── id: ID!
├── playerProfileId: ID!
├── shopProductId: ID!
├── quantity: Float!
└── createdAt: DateTime!`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="pagination">
              <Accordion.Control>Pagination & Filtering</Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="md">
                  All list queries use cursor-based pagination with the
                  following standard interface:
                </Text>
                <GqlBlock>
                  {`# Request pagination
page: {
  first: Float    # Number of items to return (page size)
  after: String   # Cursor from the previous page's endCursor
}

# Response shape (all list queries)
{
  nodes: [T]                          # Array of results
  totalCount: Int                     # Total matching records
  pageInfo: {
    hasNextPage: Boolean              # Whether more results exist
    endCursor: String                 # Cursor for the next page
  }
}

# Common filter patterns
filter: {
  ids: [ID]                           # Filter by entity IDs
  externalIds: [String]               # Filter by external IDs
}

# Metadata format (shared across all entities)
metadata: [{
  property: String!                   # Key
  value: String!                      # Value
}]
# When creating/updating, pass the full metadata array — it replaces previous metadata entirely.`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata (Data Service)"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            In addition to the inline <Code>metadata</Code> key-value pairs on
            gamification entities (currencies, items, shops, etc.), shops also
            support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields (Data service)
              </Text>{" "}
              — Structured, typed fields defined in Data &gt; Custom Fields for
              the <Code>Shop</Code> object type. Supports validation,
              visibility, and editability controls.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata (Data service)
              </Text>{" "}
              — Flexible key-value store with typed entries (String, Boolean,
              URL, Image URL) and visibility settings (Public or Private).
              Managed via the shop detail screen or GraphQL API.
            </List.Item>
          </List>
          <GqlBlock>
            {`# Query custom field values for a shop
query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}
# objectType: "Shop"

# Query metadata for a shop (Data service)
query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
}
